<script lang="ts" setup>
import { computed, ComputedRef, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate, ValidationArgs } from '@vuelidate/core';

import { useAuthStore, useUiStore } from '@/stores';
import { UserService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    required,
    maxLength,
    minLength,
    containsUppercase,
    containsLowercase,
    containsNumber,
    containsSpecial,
    sameAs,
} from '@/utils/validators';

import { UpdatePasswordFormState, UserModel } from '@/types';
import { AxiosResponse } from 'axios';

const imageLoginPath = '/img/slogan-login.png';
const logoPath = '/img/logomob.png';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();

const formState = reactive<UpdatePasswordFormState>({
    password: '',
    confirmPassword: '',
});

const token = computed(() => route.query.token as string | undefined);

const rules = computed<ValidationArgs<UpdatePasswordFormState>>(() => ({
    password: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(250),
        containsUppercase,
        containsLowercase,
        containsNumber,
        containsSpecial,
    },
    confirmPassword: {
        required,
        sameAsPassword: sameAs(formState.password, t('user.labels.password')),
    },
}));

const v$ = useVuelidate(rules, formState);

const authStore = useAuthStore();
const user: ComputedRef<UserModel | undefined> = computed(() => authStore.userModel);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const handlePasswordUpdate = async () => {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    try {
        setLoading(true);

        if (token.value) {
            const { data }: AxiosResponse = await UserService.resetPassword({
                token: token.value,
                password: formState.password,
            });

            if (data.success) {
                router.push({ name: 'Login' }).then(() => {
                    toast.success(data.message);
                });
            }
        } else {
            const { data }: AxiosResponse = await UserService.overwritePassword(formState);

            if (data.success) {
                localStorage.removeItem('tokenTemp');
                router.push({ name: 'Login' }).then(() => {
                    toast.success(data.message);
                });
            }
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    if (!authStore.userNeedsUpdatePassword) {
        if (user.value !== undefined && user.value.requiresTwoFactor) {
            await router.push({ name: 'TwoFaAuthenticator' });
            return;
        } else if (token.value === null) {
            await router.push({ name: 'Home' });
        }
    }
});
</script>

<template>
    <div class="grid w-full grid-cols-1 sm:grid-cols-2">
        <div class="bg-primary relative hidden w-full items-center justify-center sm:flex">
            <img
                :src="imageLoginPath"
                class="h-[150px] xl:h-[250px]"
                :alt="$t('authentication.logoAlt')"
            />
        </div>

        <div
            class="flex w-full flex-col items-center bg-gray-50 px-8 pt-20 sm:items-start sm:justify-center sm:pt-0 lg:px-28 xl:px-40"
        >
            <img class="mb-8 w-[225px]" :src="logoPath" :alt="$t('authentication.logoAlt')" />

            <h3 class="mb-8 text-2xl font-semibold text-gray-700">
                {{
                    user?.isPasswordExpired
                        ? $t('authentication.expiredPassword.title')
                        : $t('authentication.updatePassword.title')
                }}
            </h3>

            <p class="mb-4 text-sm">
                {{
                    user?.isPasswordExpired
                        ? $t('authentication.expiredPassword.description')
                        : $t('authentication.updatePassword.description')
                }}
            </p>

            <ul class="mb-10 list-decimal pl-6 text-sm">
                <li>{{ $t('authentication.updatePassword.rule.size') }}</li>
                <li>{{ $t('authentication.updatePassword.rule.upperCase') }}</li>
                <li>{{ $t('authentication.updatePassword.rule.lowerCase') }}</li>
                <li>{{ $t('authentication.updatePassword.rule.number') }}</li>
                <li>{{ $t('authentication.updatePassword.rule.special') }}</li>
            </ul>

            <form class="w-full space-y-4" @submit.prevent="handlePasswordUpdate">
                <UiTextInput
                    v-model="formState.password"
                    name="password"
                    type="password"
                    :label="$t('authentication.updatePassword.label.password')"
                    inputClass="text-base px-4 py-3"
                    autocomplete="password"
                    :errors="v$.password?.$errors"
                    @blur="v$.password?.$touch"
                />

                <UiTextInput
                    v-model="formState.confirmPassword"
                    name="confirmPassword"
                    type="password"
                    :label="$t('authentication.updatePassword.label.confirmPassword')"
                    inputClass="text-base px-4 py-3"
                    autocomplete="off"
                    :errors="v$.confirmPassword?.$errors"
                    @blur="v$.confirmPassword?.$touch"
                />

                <div class="flex flex-row-reverse text-sm">
                    <span
                        class="text-primary hover:text-primary-darker cursor-pointer text-sm font-medium"
                        @click="router.push({ name: 'Login' })"
                    >
                        {{ $t('core.actions.Back') }}
                    </span>
                </div>
                <div>
                    <UiButton type="submit" variant="primary" class="w-full">
                        <span class="block">{{ $t('login.form.signIn') }}</span>
                    </UiButton>
                </div>
            </form>
        </div>
    </div>
</template>
